import { BenchmarkConfig } from '../api/master-data-service';
import {
  Benchmark,
  InternalBenchmarkTypes,
  TimeBenchmarkTypes,
} from '../benchmark/types';
import { ServiceData } from '../components/charts/ByDimensionChart';
import { Granularity } from '../date-manager/date-manager-constants';
import { dateManagerService } from '../date-manager/date-manager-service';
import { TimeSliderConfig } from '../filter/filter-store';
import {
  endOfDate,
  getSamePrecedingPeriodTimeSliderConfig,
  getSpecificPeriodTimeSliderConfig,
  getTimeSliderConfigForQtAndYearBenchmark,
} from '../filter/utils';
import { uuid } from '../utilFunctions/utils';
import { ApiMasterDataQueryFilterItem } from '../v2/types';
import { V2Service } from './utils';

export enum ServiceResponseTypes {
  PRIMARY = 'primary',
  BENCHMARK = 'benchmark',
  FORECAST = 'forecast',
}

export interface BenchmarkWrapperServiceInputs extends ServiceData {
  selectedBenchmark: Benchmark | null;
  onlyProvide?: ServiceResponseTypes;
  serviceCacheKey: string;
  timeSliderConfig: TimeSliderConfig;
}

interface InputsAndFilters {
  inputs: any;
  filters: ApiMasterDataQueryFilterItem[];
}

const getBenchmarkGranularityFromSelectedBenchmark = (
  selectedBenchmark: Benchmark
) => {
  if (selectedBenchmark.value === TimeBenchmarkTypes.YEAR) {
    return Granularity.YEAR;
  } else if (selectedBenchmark.value === TimeBenchmarkTypes.FINQUARTER) {
    return Granularity.QUARTER;
  } else {
    throw new Error(
      "Don't know how to handle this benchmark type in getBenchmarkGranularityFromSelectedBenchmark function."
    );
  }
};

const getModifiedInputsAndFiltersForQtAndYearBenchmark = (
  primaryInputs: any,
  primaryFilters: ApiMasterDataQueryFilterItem[],
  timeSliderConfig: TimeSliderConfig,
  selectedBenchmark: Benchmark
) => {
  let benchmarkInputs: any = { ...primaryInputs };
  let benchmarkFilters: ApiMasterDataQueryFilterItem[] = primaryFilters;

  const benchmarkGranularity =
    getBenchmarkGranularityFromSelectedBenchmark(selectedBenchmark);
  const { date, timeSliderConfig: timeSliderConfigFromServiceInputs } =
    primaryInputs as {
      timeSliderConfig: TimeSliderConfig;
      date: string;
    };

  if (timeSliderConfigFromServiceInputs) {
    benchmarkInputs = {
      ...benchmarkInputs,
      timeSliderConfig: getTimeSliderConfigForQtAndYearBenchmark(
        timeSliderConfigFromServiceInputs,
        benchmarkGranularity
      ),
    };
    benchmarkFilters = primaryFilters;
  }
  if (date) {
    const { granularity, firstMonthOfYear } = timeSliderConfig;
    const { parseApiDate, formatDateApi } = dateManagerService.getInstance();
    const benchmarkDate = formatDateApi(
      endOfDate(
        parseApiDate(date).subtract(1, benchmarkGranularity),
        granularity,
        firstMonthOfYear
      )
    );
    benchmarkInputs = { ...benchmarkInputs, date: benchmarkDate };
    benchmarkFilters = primaryFilters;
  }
  return {
    inputs: benchmarkInputs,
    filters: benchmarkFilters,
  };
};

const getModifiedInputsAndFiltersForPriorPeriodBenchmark = (
  primaryInputs: any,
  primaryFilters: ApiMasterDataQueryFilterItem[],
  timeSliderConfig: TimeSliderConfig
) => {
  let benchmarkInputs: any = { ...primaryInputs };
  let benchmarkFilters: ApiMasterDataQueryFilterItem[] = primaryFilters;

  const { date, timeSliderConfig: timeSliderConfigFromServiceInputs } =
    primaryInputs as {
      timeSliderConfig: TimeSliderConfig;
      date: string;
    };
  const benchmarkTimeSliderConfig =
    getSamePrecedingPeriodTimeSliderConfig(timeSliderConfig);

  if (timeSliderConfigFromServiceInputs) {
    benchmarkInputs = {
      ...benchmarkInputs,
      timeSliderConfig: benchmarkTimeSliderConfig,
    };
  }
  if (date) {
    benchmarkInputs = {
      ...benchmarkInputs,
      date: benchmarkTimeSliderConfig.endDate,
    };
  }
  return {
    inputs: benchmarkInputs,
    filters: benchmarkFilters,
  };
};

export const getModifiedInputsAndFiltersForBenchmark = (
  primaryInputs: any,
  primaryFilters: ApiMasterDataQueryFilterItem[],
  separateBenchmarkFilters: ApiMasterDataQueryFilterItem[],
  timeSliderConfig: TimeSliderConfig,
  selectedBenchmark: Benchmark
): InputsAndFilters => {
  let benchmarkInputs: any;
  let benchmarkFilters: ApiMasterDataQueryFilterItem[];
  let benchmarkConfig: BenchmarkConfig;
  switch (selectedBenchmark.value) {
    case InternalBenchmarkTypes.MyPop:
      benchmarkInputs = primaryInputs;
      benchmarkFilters = separateBenchmarkFilters || [];
      break;
    case InternalBenchmarkTypes.CompanyTotal:
      benchmarkConfig = {
        limitedToPermittedPopulation: false,
      };
      benchmarkInputs = { ...primaryInputs, benchmarkConfig };
      benchmarkFilters = [];
      break;
    case InternalBenchmarkTypes.CompanySameFilters:
      benchmarkConfig = {
        limitedToPermittedPopulation: false,
      };
      benchmarkInputs = { ...primaryInputs, benchmarkConfig };
      benchmarkFilters = primaryFilters || [];
      break;
    case TimeBenchmarkTypes.YEAR:
    case TimeBenchmarkTypes.FINQUARTER:
      const { inputs, filters } =
        getModifiedInputsAndFiltersForQtAndYearBenchmark(
          primaryInputs,
          primaryFilters,
          timeSliderConfig,
          selectedBenchmark
        );
      benchmarkInputs = inputs;
      benchmarkFilters = filters;
      break;
    case TimeBenchmarkTypes.PRIOR_PERIOD:
      const { inputs: priorPeriodInputs, filters: priorPeriodFilters } =
        getModifiedInputsAndFiltersForPriorPeriodBenchmark(
          primaryInputs,
          primaryFilters,
          timeSliderConfig
        );
      benchmarkInputs = priorPeriodInputs;
      benchmarkFilters = priorPeriodFilters;
      break;
    case TimeBenchmarkTypes.SPECIFIC_PERIOD:
      const {
        specificPeriodBenchmarkStartDate,
        specificPeriodBenchmarkEndDate,
      } = timeSliderConfig;
      const specificPeriodRange = {
        startDate: specificPeriodBenchmarkStartDate,
        endDate: specificPeriodBenchmarkEndDate,
      };
      const specificPeriodBenchmarkTimeSliderConfig =
        getSpecificPeriodTimeSliderConfig(
          specificPeriodRange,
          timeSliderConfig
        );
      benchmarkInputs = {
        ...primaryInputs,
        timeSliderConfig: specificPeriodBenchmarkTimeSliderConfig,
      };
      benchmarkFilters = primaryFilters;
      break;
    default:
      benchmarkInputs = primaryInputs;
      benchmarkFilters = primaryFilters;
      break;
  }
  return {
    inputs: benchmarkInputs,
    filters: benchmarkFilters,
  };
};

export const BenchmarkWrapperService: V2Service<
  BenchmarkWrapperServiceInputs,
  any,
  ApiMasterDataQueryFilterItem
> = async (
  inputs,
  _,
  latestVersions,
  legacyStores,
  legacyDomainScopedStores
) => {
  const {
    service,
    serviceInputs,
    filters: serviceFilters,
    benchmarkFilters: separateBenchmarkFilters = [],
    selectedBenchmark,
    timeSliderConfig,
    onlyProvide,
  } = inputs;

  const getPrimaryResponse = async () => {
    return service(
      serviceInputs,
      serviceFilters,
      latestVersions,
      legacyStores,
      legacyDomainScopedStores
    );
  };
  const getBenchmarkResponse = async () => {
    if (selectedBenchmark) {
      const { inputs: benchmarkInputs, filters: benchmarkFilters } =
        getModifiedInputsAndFiltersForBenchmark(
          serviceInputs,
          serviceFilters,
          separateBenchmarkFilters,
          timeSliderConfig,
          selectedBenchmark
        );
      return service(
        benchmarkInputs,
        benchmarkFilters,
        latestVersions,
        legacyStores,
        legacyDomainScopedStores
      );
    } else {
      return null;
    }
  };

  let response;
  if (onlyProvide === 'primary') {
    response = await getPrimaryResponse();
  } else if (onlyProvide === 'benchmark') {
    response = await getBenchmarkResponse();
  } else {
    const [primaryResponse, benchmarkResponse] = await Promise.all([
      getPrimaryResponse(),
      getBenchmarkResponse(),
    ]);
    response = {
      primaryResponse,
      benchmarkResponse,
    };
  }

  return { ...response, selectedBenchmark };
};

BenchmarkWrapperService.cacheKey = uuid();
